import axios from 'axios';
import authService from './authService';

const API_URL = '/api/files/';

// Add a request interceptor to include the auth token
axios.interceptors.request.use(
  (config) => {
    const user = authService.getCurrentUser();
    if (user && user.accessToken) {
      config.headers.Authorization = 'Bearer ' + user.accessToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

class FileService {
  // Upload configuration for large files
  getUploadConfig(onProgress = null) {
    return {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // No timeout - unlimited
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
          if (onProgress) {
            onProgress(percentCompleted);
          }
        }
      }
    };
  }

  uploadFile(file, accessLevel = 'PUBLIC', description = '', onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accessLevel', accessLevel);
    if (description) {
      formData.append('description', description);
    }

    return axios.post(API_URL + 'upload', formData, this.getUploadConfig(onProgress));
  }

  downloadFile(storedName) {
    return axios.get(API_URL + `download/${storedName}`, {
      responseType: 'blob',
    });
  }

  getFileList(page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    return axios.get(API_URL + `list?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  }

  getMyFiles(page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    return axios.get(API_URL + `my-files?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  }

  deleteFile(fileId) {
    return axios.delete(API_URL + fileId);
  }

  // Multiple file upload
  uploadMultipleFiles(files, accessLevel = 'PUBLIC', description = '', parentFolderId = null, folderPath = null) {
    const formData = new FormData();

    // Handle both FileList and Array
    const fileArray = Array.isArray(files) ? files : Array.from(files);

    for (let i = 0; i < fileArray.length; i++) {
      formData.append('files', fileArray[i]);
    }

    formData.append('accessLevel', accessLevel);
    if (description) {
      formData.append('description', description);
    }
    if (parentFolderId) {
      formData.append('parentFolderId', parentFolderId);
    }
    if (folderPath) {
      formData.append('folderPath', folderPath);
    }

    return axios.post(API_URL + 'upload-multiple', formData, this.getUploadConfig());
  }

  // Single file upload with folder support
  uploadFileWithFolder(file, accessLevel = 'PUBLIC', description = '', parentFolderId = null, folderPath = null, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accessLevel', accessLevel);
    if (description) {
      formData.append('description', description);
    }
    if (parentFolderId) {
      formData.append('parentFolderId', parentFolderId);
    }
    if (folderPath) {
      formData.append('folderPath', folderPath);
    }

    return axios.post(API_URL + 'upload', formData, this.getUploadConfig(onProgress));
  }

  // Create folder
  createFolder(folderName, accessLevel = 'PUBLIC', description = '', parentFolderId = null) {
    const formData = new FormData();
    formData.append('folderName', folderName);
    formData.append('accessLevel', accessLevel);
    if (description) {
      formData.append('description', description);
    }
    if (parentFolderId) {
      formData.append('parentFolderId', parentFolderId);
    }

    return axios.post(API_URL + 'create-folder', formData);
  }

  // Get folder contents
  getFolderContents(folderId = null) {
    const url = folderId ? `folder/${folderId}` : 'folder';
    return axios.get(API_URL + url);
  }

  // Delete folder
  deleteFolder(folderId) {
    return axios.delete(API_URL + `folder/${folderId}`);
  }

  // Admin functions
  getAllFilesForAdmin(page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') {
    return axios.get(API_URL + `admin/all-files?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
  }

  grantUserAccess(fileId, userId, accessType = 'READ', expiresAt = null) {
    const params = new URLSearchParams();
    params.append('userId', userId);
    params.append('accessType', accessType);
    if (expiresAt) {
      params.append('expiresAt', expiresAt);
    }

    return axios.post(API_URL + `admin/${fileId}/grant-user-access`, params);
  }

  revokeUserAccess(fileId, userId) {
    return axios.delete(API_URL + `admin/${fileId}/revoke-user-access?userId=${userId}`);
  }

  getFileAccessUsers(fileId) {
    return axios.get(API_URL + `admin/${fileId}/access-users`);
  }

  getAllUsers() {
    return axios.get(API_URL + 'admin/users');
  }

  getUploadInfo() {
    return axios.get(API_URL + 'upload-info');
  }

  getStorageInfo() {
    return axios.get(API_URL + 'storage-info');
  }
}

export default new FileService();